<template>
  <AuthenticatedView
    :loading="loading && !result"
    :current-user="currentUser"
    :has-result="!!result"
  >
    <AppHeader
      v-if="currentUser && !embedded"
      :user="currentUser"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div
      :class="embedded ? 'dashboard-panel' : 'card'"
      :aria-labelledby="embedded ? 'boards-panel-title' : undefined"
    >
      <breadcrumbs v-if="!embedded" :items="breadcrumbsItems" />
      <div :class="embedded ? 'dashboard-panel-header' : 'header-container'">
        <h3 v-if="embedded" id="boards-panel-title" class="dashboard-panel-title">
          <template v-if="scope === 'project'">
            {{ s__('Boards|Project boards') }}
          </template>
          <template v-else>
            {{ s__('Boards|My boards') }}
          </template>
        </h3>
        <h2 v-else class="header-title">
          <span style="display: inline-flex; align-items: center; gap: 8px">
            <template v-if="scope === 'project'">
              {{ projectTitle || s__('Boards|Boards') }}
            </template>
            <template v-else>
              {{ s__('Boards|My Boards') }}
            </template>
            <button
              type="button"
              class="info-icon-button"
              @click.stop="showInfoModal = true"
              :title="s__('Boards|Boards Information')"
              aria-label="Boards Information"
            >
              <InfoIcon />
            </button>
          </span>
        </h2>
        <div class="header-actions">
          <button
            v-if="canCreateBoard"
            type="button"
            class="btn-link btn-primary"
            @click="showCreateModal = true"
          >
            <span class="btn-text">{{ s__('Boards|New Board') }}</span>
            <span class="btn-icon"><PlusIcon /></span>
          </button>
        </div>
      </div>
      <div :class="{ 'dashboard-panel-body': embedded }">
        <div v-if="loading">{{ __('Loading…') }}</div>
        <div v-else-if="!boardsEnabled" class="task-info-container">
          <div class="confidential-message">
            <div class="confidential-icon">📋</div>
            <h3>{{ s__('Boards|Boards Feature Disabled') }}</h3>
            <p>
              <template v-if="scope === 'project'">
                {{
                  s__(
                    'Boards|Boards feature is disabled for this project. It can be enabled in project settings.'
                  )
                }}
              </template>
              <template v-else>
                {{
                  s__(
                    'Boards|Boards feature is disabled. It can be enabled in your profile settings.'
                  )
                }}
              </template>
            </p>
          </div>
        </div>
        <div v-else>
          <div v-if="error" class="error" style="margin-top: 8px">{{ error }}</div>
          <div v-if="boards.length === 0 && !error" class="muted" style="margin-top: 8px">
            {{ s__('Boards|No boards yet') }}
          </div>

          <ul v-else class="boards-list">
            <li v-for="board in boards" :key="board.id" class="board-item">
              <a
                :href="boardUrl(board)"
                class="board-item-overlay-link"
                :aria-label="board.title"
              />
              <div class="board-item-content">
                <div class="board-item-main">
                  <strong class="board-item-title">{{ board.title }}</strong>
                  <div v-if="board.description" class="board-item-description muted">
                    {{ board.description }}
                  </div>
                  <div class="board-item-meta muted">
                    <span
                      v-if="board.boardColumns && board.boardColumns.length > 0"
                      class="board-item-meta-pill"
                    >
                      {{ n__('Boards|%d column', '%d columns', board.boardColumns.length) }}
                    </span>
                    <span v-else class="board-item-meta-pill">{{ s__('Boards|No columns') }}</span>
                  </div>
                </div>
                <div v-if="board.canManageBoard" class="board-item-actions">
                  <button
                    type="button"
                    class="board-item-action-btn"
                    @click.stop="editBoard(board)"
                  >
                    <span class="btn-text">{{ s__('Boards|Edit') }}</span>
                    <span class="btn-icon"><EditIcon /></span>
                  </button>
                  <button
                    type="button"
                    class="board-item-action-btn board-item-action-danger"
                    @click.stop="confirmDeleteBoard(board)"
                  >
                    <span class="btn-text">{{ s__('Boards|Delete') }}</span>
                    <span class="btn-icon"><TrashIcon /></span>
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Create/Edit Board Modal -->
      <div
        v-if="showCreateModal || editingBoard"
        class="modal-overlay"
        style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        "
      >
        <div
          class="modal-content"
          style="
            background: #fff;
            border-radius: 8px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
          "
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            "
          >
            <h3 style="margin: 0">
              {{ editingBoard ? s__('Boards|Edit Board') : s__('Boards|New Board') }}
            </h3>
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: none;
                padding: 4px 8px;
                font-size: 20px;
                cursor: pointer;
              "
              @click="closeModal"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <form @submit.prevent="handleSubmit">
            <div class="row">
              <div>
                <label> {{ s__('Boards|Title') }} * </label>
                <input v-model="boardForm.title" type="text" required />
              </div>
            </div>
            <div class="row">
              <div>
                <label>
                  {{ s__('Boards|Description') }}
                </label>
                <textarea v-model="boardForm.description" rows="3" />
              </div>
            </div>
            <div class="row board-modal-hint-row">
              <div class="board-modal-hint">
                <span class="board-modal-hint-icon"><InfoIcon /></span>
                <span class="board-modal-hint-text">
                  {{
                    s__(
                      'Boards|Columns and task filtering/sorting/grouping are managed on the board page.'
                    )
                  }}
                </span>
              </div>
            </div>
            <div v-if="formError" class="error" style="margin-bottom: 16px; font-size: 13px">
              {{ formError }}
            </div>
            <div style="display: flex; gap: 8px; justify-content: flex-end">
              <button type="button" class="btn-sm btn-secondary" @click="closeModal">
                {{ s__('Boards|Cancel') }}
              </button>
              <button type="submit" class="btn-sm" :disabled="creating || updating">
                <span v-if="creating || updating">{{ s__('Boards|Saving...') }}</span>
                <span v-else>{{ s__('Boards|Save') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div
        v-if="deletingBoard"
        class="modal-overlay"
        style="
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        "
      >
        <div
          class="modal-content"
          style="background: #fff; border-radius: 8px; padding: 24px; max-width: 400px; width: 90%"
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            "
          >
            <h3 style="margin: 0">{{ s__('Boards|Delete Board') }}</h3>
            <button
              type="button"
              class="btn-sm"
              style="
                color: #666;
                background: transparent;
                border: none;
                padding: 4px 8px;
                font-size: 20px;
                cursor: pointer;
              "
              @click="deletingBoard = null"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p style="margin: 0 0 16px 0">
            {{
              s__(
                'Boards|Are you sure you want to delete this board? This action cannot be undone.'
              )
            }}
          </p>
          <div style="display: flex; gap: 8px; justify-content: flex-end">
            <button type="button" class="btn-sm btn-secondary" @click="deletingBoard = null">
              {{ s__('Boards|Cancel') }}
            </button>
            <button
              type="button"
              class="btn-sm btn-danger"
              :disabled="deleting"
              @click="confirmDelete"
            >
              <span v-if="deleting">{{ s__('Boards|Deleting...') }}</span>
              <span v-else>{{ s__('Boards|Delete') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <BoardsIndexInfoModal :visible="showInfoModal" @close="showInfoModal = false" />
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, n__ } from '@/locale'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import PlusIcon from '../common/icons/PlusIcon.vue'
import EditIcon from '../common/icons/EditIcon.vue'
import TrashIcon from '../common/icons/TrashIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import AppHeader from '../common/AppHeader.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import BoardsIndexInfoModal from './BoardsIndexInfoModal.vue'

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: value => ['project', 'user'].includes(value),
  },
  projectId: String,
  userId: String,
  chatId: String,
  fromId: String,
  embedded: {
    type: Boolean,
    default: false,
  },
})
import { removeInitialLoading } from '@/utils/loadingScreen'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

// GraphQL Queries
const PROJECT_BOARDS_QUERY = gql`
  query ProjectBoards($projectId: ID!) {
    currentUser {
      id
      name
      username
    }
    project(id: $projectId) {
      id
      title
      canUpdateProject
      boardsEnabled
      boards {
        id
        title
        description
        position
        archived
        isProjectBoard
        isPersonalBoard
        canManageBoard
        orderBy
        orderDirection
        boardColumns {
          id
          title
        }
      }
    }
  }
`

const USER_BOARDS_QUERY = gql`
  query UserBoards {
    currentUser {
      id
      name
      username
      boardsEnabled
      boards {
        id
        title
        description
        position
        archived
        isProjectBoard
        isPersonalBoard
        canManageBoard
        orderBy
        orderDirection
        boardColumns {
          id
          title
        }
      }
    }
  }
`

// GraphQL Mutations
const CREATE_BOARD = gql`
  mutation CreateBoard(
    $projectId: ID
    $title: String!
    $description: String
    $orderBy: String
    $orderDirection: String
  ) {
    createBoard(
      projectId: $projectId
      title: $title
      description: $description
      orderBy: $orderBy
      orderDirection: $orderDirection
    ) {
      board {
        id
        title
        description
        position
        archived
        isProjectBoard
        isPersonalBoard
        canManageBoard
        orderBy
        orderDirection
        boardColumns {
          id
          title
        }
      }
      errors
    }
  }
`

const UPDATE_BOARD = gql`
  mutation UpdateBoard(
    $id: ID!
    $title: String
    $description: String
    $orderBy: String
    $orderDirection: String
  ) {
    updateBoard(
      id: $id
      title: $title
      description: $description
      orderBy: $orderBy
      orderDirection: $orderDirection
    ) {
      board {
        id
        title
        description
        position
        archived
        isProjectBoard
        isPersonalBoard
        canManageBoard
        orderBy
        orderDirection
        boardColumns {
          id
          title
        }
      }
      errors
    }
  }
`

const DELETE_BOARD = gql`
  mutation DeleteBoard($id: ID!) {
    deleteBoard(id: $id) {
      board {
        id
      }
      errors
    }
  }
`

// Query variables
const queryVariables = computed(() => {
  if (props.scope === 'project') {
    return { projectId: props.projectId }
  }
  return {}
})

// Select query based on scope
const selectedQuery = computed(() => {
  return props.scope === 'project' ? PROJECT_BOARDS_QUERY : USER_BOARDS_QUERY
})

// Setup query
const { result, loading, error, refetch } = useQuery(selectedQuery, queryVariables, () => ({
  enabled: props.scope === 'project' ? !!props.projectId : true,
}))

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

// Current user for AppHeader
const currentUser = computed(() => result.value?.currentUser || null)

// Mutations
const { mutate: createBoardMutation } = useMutation(CREATE_BOARD)
const { mutate: updateBoardMutation } = useMutation(UPDATE_BOARD)
const { mutate: deleteBoardMutation } = useMutation(DELETE_BOARD)

// State
const boards = ref([])
const projectTitle = ref('')
const canCreateBoard = ref(false)
const boardsEnabled = ref(true) // Default to true for personal boards
const showCreateModal = ref(false)
const showInfoModal = ref(false)
const editingBoard = ref(null)
const deletingBoard = ref(null)

// Lock body scroll when modals are open
useBodyScrollLock(computed(() => showCreateModal.value || !!editingBoard.value))
useBodyScrollLock(computed(() => !!deletingBoard.value))
const creating = ref(false)
const updating = ref(false)
const deleting = ref(false)
const formError = ref('')

// Form data
const boardForm = ref({
  title: '',
  description: '',
})

// Breadcrumbs
const breadcrumbsItems = computed(() => {
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''

  const items = [{ label: s__('Boards|Main'), url: `/dashboard${chat}${from}` }]
  if (props.scope === 'project' && projectTitle.value && props.projectId) {
    items.push({
      label: projectTitle.value,
      url: `/projects/${props.projectId}/dashboard${chat}${from}`,
    })
  }
  items.push({ label: s__('Boards|Boards') })
  return items
})

// Watch query result
watch(
  result,
  data => {
    if (props.scope === 'project') {
      if (data?.project) {
        projectTitle.value = data.project.title || ''
        canCreateBoard.value = data.project.canUpdateProject || false
        boardsEnabled.value = data.project.boardsEnabled || false
        boards.value = data.project.boards || []
      }
    } else {
      if (data?.currentUser) {
        canCreateBoard.value = true // Users can always create personal boards
        boardsEnabled.value = data.currentUser.boardsEnabled ?? true
        boards.value = data.currentUser.boards || []
      }
    }
  },
  { immediate: true }
)

// Methods
function boardUrl(board) {
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''

  if (props.scope === 'project') {
    return `/projects/${props.projectId}/boards/${board.id}${chat}${from}`
  }
  return `/boards/${board.id}${chat}${from}`
}

function editBoard(board) {
  editingBoard.value = board
  boardForm.value = {
    title: board.title || '',
    description: board.description || '',
  }
  formError.value = ''
}

function confirmDeleteBoard(board) {
  deletingBoard.value = board
}

function closeModal() {
  showCreateModal.value = false
  editingBoard.value = null
  boardForm.value = {
    title: '',
    description: '',
  }
  formError.value = ''
}

async function handleSubmit() {
  formError.value = ''

  if (editingBoard.value) {
    // Update board
    updating.value = true
    try {
      const { data } = await updateBoardMutation({
        id: editingBoard.value.id,
        title: boardForm.value.title,
        description: boardForm.value.description || null,
      })

      if (data?.updateBoard?.errors?.length) {
        const errorMessages = data.updateBoard.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        formError.value = errorMessages
        updating.value = false
        return
      }

      closeModal()
      await refetch()
    } catch (e) {
      formError.value = e.message || s__('Boards|Failed to update board')
    } finally {
      updating.value = false
    }
  } else {
    // Create board
    creating.value = true
    try {
      const variables = {
        title: boardForm.value.title,
        description: boardForm.value.description || null,
      }
      if (props.scope === 'project') {
        variables.projectId = props.projectId
      }

      const { data } = await createBoardMutation(variables)

      if (data?.createBoard?.errors?.length) {
        const errorMessages = data.createBoard.errors
          .map(e => (typeof e === 'string' ? e : e.message || String(e)))
          .join(', ')
        formError.value = errorMessages
        creating.value = false
        return
      }

      closeModal()
      await refetch()
    } catch (e) {
      formError.value = e.message || s__('Boards|Failed to create board')
    } finally {
      creating.value = false
    }
  }
}

async function confirmDelete() {
  if (!deletingBoard.value) return

  deleting.value = true
  try {
    const { data } = await deleteBoardMutation({
      id: deletingBoard.value.id,
    })

    if (data?.deleteBoard?.errors?.length) {
      const errorMessages = data.deleteBoard.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      alert(errorMessages)
      deleting.value = false
      return
    }

    deletingBoard.value = null
    await refetch()
  } catch (e) {
    alert(e.message || s__('Boards|Failed to delete board'))
  } finally {
    deleting.value = false
  }
}
</script>

<style src="../../styles/forms.css"></style>
<style scoped>
.board-item {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  margin-bottom: 10px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.boards-list {
  list-style: none;
  padding: 0;
  margin-top: 12px;
}

.board-item-content {
  position: relative;
  z-index: 2;
  pointer-events: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
}

.board-item-main {
  min-width: 0;
  flex: 1;
}

.board-item-title {
  display: block;
  font-size: 15px;
  line-height: 1.35;
  color: #1f2937;
}

.board-item-description {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
}

.board-item-meta {
  margin-top: 8px;
  font-size: 12px;
}

.board-item-meta-pill {
  display: inline-flex;
  align-items: center;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 2px 8px;
}

.board-item-overlay-link {
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: 10px;
}

.board-item-actions {
  pointer-events: auto;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.board-item-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 9px;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.board-item-action-btn .btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.board-item-action-btn:hover {
  background: #f9fafb;
  border-color: #cbd5e1;
}

.board-item-action-danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.board-item-action-danger:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.board-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
}

.board-modal-hint {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 12px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #dbeafe;
  color: #1e3a8a;
}

.board-modal-hint-row {
  margin-top: 12px !important;
  margin-bottom: 18px !important;
}

.board-modal-hint-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.board-modal-hint-text {
  font-size: 13px;
  line-height: 1.5;
}

/* Warning message styles (similar to confidential task message) */
.task-info-container {
  padding: 16px;
}

.confidential-message {
  text-align: center;
  padding: 48px 24px;
  max-width: 600px;
  margin: 0 auto;
}

.confidential-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.confidential-message h3 {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 12px 0;
}

.confidential-message p {
  font-size: 14px;
  color: #757575;
  line-height: 1.6;
  margin: 0;
}

/* Header container - responsive layout */
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.header-title {
  margin: 0;
  flex: 1;
  min-width: 0; /* Allow text truncation */
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* Button styles inherit from forms.css */
.btn-link .btn-icon {
  color: white;
  filter: none;
}

.btn-link .btn-icon:hover,
.btn-link-secondary .btn-icon:hover {
  background: transparent;
  color: inherit;
}

.info-icon-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.info-icon-button:hover {
  background-color: #f0f0f0;
}

.btn-icon {
  display: none;
}

/* Mobile responsive styles - matching projects/tasks/Index.vue exactly */
@media (max-width: 640px) {
  .btn-text {
    display: none;
  }

  .btn-icon {
    display: inline;
  }

  .board-item-action-btn {
    min-width: 32px;
    padding: 5px 8px;
    justify-content: center;
  }

  .board-item-action-btn .btn-icon {
    width: 16px;
    height: 16px;
  }

  .btn-link {
    min-width: 36px;
    padding: 8px;
    text-align: center;
  }

  .board-item-content {
    padding: 12px;
  }
}
</style>
